import { useEffect, useState } from "react";
import { ArrowRight, ArrowLeft } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { getExercises } from "../../../api/exercises/exercises";

const ExerciseListPage = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [show, setShow] = useState(false);
  type Exercise = {
    id: any;
    name: any;
    duration: any;
    countReps: any;
    countSets: any;
    index: number;
  };
  const [exercises, setExercises] = useState<Exercise[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setShow(true);
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const dateStr = `${year}-${month}-${day}`;
    const from = dateStr;
    const to = dateStr;

    const fetchExercises = async () => {
      try {
        setLoading(true);
        const res = await getExercises({ from, to });

        const currentLang =
          typeof window !== "undefined"
            ? localStorage.getItem("i18nextLng") || "en"
            : "en";

        const allExercises = Object.values(res || {})
          .flat()
          .map((item: any) => {
            const contents = item.therapyItem?.contents || [];
            let matchedContent = contents?.find(
              (c: any) => c.languageCode === currentLang
            );
            if (!matchedContent) {
              matchedContent = contents?.find((c: any) => !!c && !!c.name);
            }
            const attributeTags =
              item?.therapyItem?.attributeTags || item?.attributeTags;
            const videoTypeTag =
              attributeTags &&
              attributeTags.video_type &&
              Array.isArray(attributeTags.video_type)
                ? attributeTags.video_type[0]
                : null;
            const videoType = videoTypeTag?.value?.en || "";
            const isDurationMode =
              videoType === "ExerciseA" || videoType === "ExerciseB";
            const timeVideoExercise = item.therapyItem?.config?.duration || 0;
            const countReps = item.repetitions;
            const countSets = item.sets;
            const timeBetweenSets = item.restTime;
            let calculatedDuration: number | string = "-";
            if (
              typeof countReps === "number" &&
              typeof countSets === "number" &&
              typeof timeBetweenSets === "number" &&
              typeof timeVideoExercise === "number"
            ) {
              let totalRepTime = 0;
              if (isDurationMode) {
                totalRepTime = timeVideoExercise * countSets;
              } else {
                totalRepTime = timeVideoExercise * countReps * countSets;
              }
              const numberOfRests = countSets > 1 ? countSets - 1 : 0;
              const totalRestTime = timeBetweenSets * numberOfRests;
              calculatedDuration = totalRepTime + totalRestTime;
            }
            return {
              contraindications: Array.isArray(
                item?.prescription?.contraindications
              )
                ? item.prescription.contraindications
                : [],
              isSubmitted: item.isSubmitted,
              id: item.id,
              name: matchedContent?.name || "-",
              duration: calculatedDuration,
              countReps: item.repetitions ?? "-",
              countSets: item.sets ?? "-",
              video: item.therapyItem?.video ?? "-",
              contents,
              timeBetweenSets: item.restTime ?? "-",
              videoCapture: item.videoCapture ?? "-",
              questions:
                item.questions && Array.isArray(item.questions)
                  ? item.questions
                  : [],
              filteredQuestions:
                item.questions && Array.isArray(item.questions)
                  ? item.questions
                      .flat()
                      .filter((q: any) => q.languageCode === currentLang)
                  : [],
              filteredFeedback:
                item.referItem && Array.isArray(item.referItem)
                  ? item.referItem
                      .flat()
                      .filter((q: any) => q.languageCode === currentLang)
                  : [],
              therapyItemType: item.therapyItem?.therapyItemType || "-",
              referItems: item.referItem?.therapyItemType,
              attributeTags: item.therapyItem?.attributeTags || [],
              config: item.therapyItem?.config || {},
              timeVideoExercise: item.therapyItem?.config?.duration || "-",
              index: item.index || 0,
            };
          });
        setExercises(allExercises);
      } catch (error) {
        setExercises([]);
        console.error("Error fetching exercises:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchExercises();
  }, [i18n.language]);

  return (
    <div className="flex justify-center">
      <div
        className={`w-full max-w-5xl space-y-5 mt-8 transform transition-all duration-500 pb-20 lg:pb-4 ${
          show ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
        }`}
      >
        <div className="flex items-center mb-4">
          <button
            className="mr-3 text-primary-600 hover:bg-primary-50 rounded-full p-2 transition"
            onClick={() => navigate("/")}
            aria-label="Back"
          >
            <ArrowLeft size={24} />
          </button>
          <h2 className="text-2xl font-bold text-gray-700">
            {t("title.treatmentPlan") || "Exercise List"}
          </h2>
        </div>
        {loading ? (
          <div className="text-center text-gray-400 py-10">
            {t("loading") || "Loading..."}
          </div>
        ) : exercises.length === 0 ? (
          <div className="text-center text-gray-400 py-10">
            {t("title.noData") || "No exercises found."}
          </div>
        ) : (
          [...exercises]
            .sort((a, b) => (a.index ?? 0) - (b.index ?? 0))
            .map((ex: any, idx: number) => (
              <div
                key={ex.id || idx}
                className={`shadow px-6 py-4 flex flex-col rounded-xl border-l-4 ${
                  ex.isSubmitted
                    ? "bg-gray-100 border-gray-400"
                    : "bg-white border-primary-500"
                }`}
                style={{ boxShadow: "6px 8px 24px 4px #F6944D1F" }}
              >
                <div className="flex justify-between items-center mb-2">
                  <p className="font-semibold text-lg text-gray-800">
                    {ex.name}
                  </p>
                  <span
                    className={`text-sm ${
                      ex.isSubmitted
                        ? "text-green-600 bg-green-50"
                        : "text-blue-600 bg-blue-50"
                    } px-3 py-1 rounded-full`}
                  >
                    {ex.isSubmitted
                      ? t("title.completed") || "Completed"
                      : t("title.notCompleted") || "Not completed"}
                  </span>
                </div>
                <div className="flex items-center justify-between">
                  <div
                    className="flex flex-col gap-1 text-sm text-gray-500"
                    style={{ minHeight: "71px" }}
                  >
                    {ex.therapyItemType === "questions" ? (
                      <div className="flex flex-col justify-center h-full">
                        <span>
                          <span className="font-semibold">
                            {t("title.total")}:
                          </span>{" "}
                          {ex.filteredQuestions?.length || 0}
                        </span>
                      </div>
                    ) : (
                      <>
                        <span>
                          <span className="font-semibold">
                            {t("title.duration") || "Duration"}:
                          </span>{" "}
                          {ex.duration} {ex.duration !== "-" ? "seconds" : ""}
                        </span>
                        <span>
                          <span className="font-semibold">
                            {t("title.repetitions") || "Repetitions"}:
                          </span>{" "}
                          {ex?.attributeTags &&
                          Array.isArray(ex.attributeTags) &&
                          (ex.attributeTags?.find(
                            (tag: any) => tag?.key === "video_type"
                          )?.value?.en === "ExerciseA" ||
                            ex.attributeTags?.find(
                              (tag: any) => tag?.key === "video_type"
                            )?.value?.en === "ExerciseB")
                            ? ex.timeVideoExercise
                            : ex.countReps}
                        </span>
                        <span>
                          <span className="font-semibold">
                            {t("title.sets") || "Sets"}:
                          </span>{" "}
                          {ex.countSets}
                        </span>
                      </>
                    )}
                  </div>
                  {!ex.isSubmitted && (
                    <button
                      className="text-primary-600 flex items-center gap-1 font-bold"
                      onClick={() => {
                        localStorage.setItem(
                          `exercise_${ex.id}`,
                          JSON.stringify(ex)
                        );
                        if (ex.therapyItemType === "questions") {
                          navigate(`/exercise/questions/${ex.id}`, {
                            state: ex,
                          });
                        } else {
                          navigate(`/exercise/${ex.id}`, { state: ex });
                        }
                      }}
                    >
                      <ArrowRight size={16} />
                    </button>
                  )}
                </div>
              </div>
            ))
        )}
      </div>
    </div>
  );
};

export default ExerciseListPage;
